import { Button } from "@ui-kit/Button/Button";
import { Form } from "@ui-kit/Form/Form";
import { FormField } from "@ui-kit/Form/FormField";
import { TextInput } from "@ui-kit/TextInput/TextInput";
import { FunctionComponent, useCallback, useState } from "react";
import { ShareCopyButton } from "./ShareCopyButton";
import { shareItineraryPlanner } from "@store/visitWidgetApi";
import { selectPlanId } from "@store/plan/planReducer";
import { useAppDispatch, useAppSelector } from "@store/store";
import { useTranslation } from "react-i18next";
import { logError } from "@lib/logger";
import { addToastAction } from "@store/toastReducer";
import { NameField } from "../../components/formFields/NameField/NameField";
import { FormStatus } from "../../components/formFields/FormStatus";
interface ShareHelpValues {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface ShareHelpProps {
  onClose: () => void;
}

const ShareHelp: FunctionComponent<ShareHelpProps> = ({ onClose }) => {
  const [form] = Form.useForm<ShareHelpValues>();
  const [state, setState] = useState<{ status: FormStatus }>({
    status: "idle",
  });
  const planId = useAppSelector(selectPlanId)!;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleSubmit = useCallback(
    async (values: ShareHelpValues) => {
      try {
        setState({ status: "fetching" });
        await shareItineraryPlanner(
          planId,
          values.name,
          values.email,
          values.phone,
          values.message,
        );
        dispatch(
          addToastAction({
            message: t("toasts.sharePlanSuccess"),
            type: "success",
          }),
        );
        onClose();
      } catch (error) {
        logError(error);
        dispatch(
          addToastAction({
            message: t("toasts.error"),
            type: "error",
          }),
        );
      } finally {
        setState({ status: "idle" });
      }
    },
    [dispatch, onClose, planId, t],
  );

  return (
    <>
      <Form
        form={form}
        name="register"
        layout="vertical"
        onFinish={handleSubmit}
        disabled={state.status === "fetching"}
        initialValues={{ name: "", email: "", phone: "", message: "" }}>
        <NameField disabled={state.status === "fetching"} />
        <FormField label={t("forms.emailField.label")} name="email">
          <TextInput
            placeholder={t("forms.emailField.placeholder")}
            size="large"
            disabled={state.status === "fetching"}
            name="email"
            rules={[
              {
                validator: async (_, value) => {
                  const phone = form.getFieldValue("phone");
                  if (!value && !phone) {
                    throw new Error(t("forms.contactValidation.required"));
                  }
                },
              },
            ]}
          />
        </FormField>
        <FormField label={t("forms.phoneField.label")} name="phone">
          <TextInput
            placeholder={t("forms.phoneField.placeholder")}
            size="large"
            disabled={state.status === "fetching"}
            name="phone"
            rules={[
              {
                validator: async (_, value) => {
                  const email = form.getFieldValue("email");
                  if (!value && !email) {
                    throw new Error(t("forms.contactValidation.required"));
                  }
                },
              },
            ]}
          />
        </FormField>
        <FormField
          label={t("screens.planShare.itineraryMessageField.label")}
          name="message">
          <TextInput
            placeholder={t(
              "screens.planShare.itineraryMessageField.placeholder",
            )}
            multiline={true}
            numberOfLines={4}
            size="large"
            disabled={state.status === "fetching"}
            name="message"
            rules={[
              {
                required: true,
                message: t("screens.planShare.itineraryMessageField.required"),
              },
            ]}
          />
        </FormField>
      </Form>
      <div className="share-plan-actions-container">
        <ShareCopyButton />
        <Button
          type="primary"
          onPress={form.submit}
          disabled={state.status === "fetching"}>
          {t("common.send")}
        </Button>
      </div>
    </>
  );
};

export { ShareHelp };
